/**
 * Where a stream you watch is shown (owner's report 2026-11-01: "when you
 * minimize the livestream it won't show it to you anymore"). The voice
 * room's stage says whether it is on screen; when it is not (a text
 * channel open, a phone's list or the room's chat over it) the stream
 * pop-out (StreamMini) shows the stream instead, and its "back" brings the
 * room back with that stream in focus (Discord's pop-out). The stage's
 * focus outlives the stage, so coming back shows what you left; so do its
 * other view choices per room (the people without video, the strip), and
 * the cameras you turned off for yourself last until you leave voice.
 */
import type { Snowflake } from '@neosocial/protocol';
import { create } from 'zustand';
import { useConfig } from '../store/config';
import { useVoice } from '../voice/store';

/** A tile the stage can focus: a stream, or a person (their camera). */
export type FocusKey = { kind: 'stream' | 'person'; userId: Snowflake };

/**
 * How a video tile was tapped: `wait`, a pointer's first click (it waits
 * for a possible second one before the focus moves, V-06); `key`, Enter or
 * Space (the tile keeps the keyboard focus where it moves to, V-09).
 */
export interface TileTap {
  wait?: boolean;
  key?: boolean;
}

interface StreamViewState {
  /** The voice room's stage is on screen now (its channel). */
  stageOn: Snowflake | null;
  /** The stage's focus by channel, kept while the stage is away. */
  focus: Record<Snowflake, FocusKey | null>;
  /**
   * A stream to bring back (the pop-out's "back"): the stage focuses it,
   * and a phone's room closes its chat. `n` counts the asks.
   */
  ask: { channelId: Snowflake; userId: Snowflake; n: number } | null;
  /** The pop-out folded to its bar: no video (nothing received), the sound plays on. */
  folded: boolean;
  /** By channel: people without video left off the stage (Discord's "Show Non-Video Participants" off). */
  videoOnly: Record<Snowflake, boolean>;
  /** By channel: the focus view's strip folded away. */
  stripHidden: Record<Snowflake, boolean>;
  /** Cameras you turned off for yourself (Discord's "Disable Video"), by user: none of their video comes. */
  videoOff: Record<Snowflake, true>;
}

export const useStreamView = create<StreamViewState>(() => ({
  stageOn: null,
  focus: {},
  ask: null,
  folded: false,
  videoOnly: {},
  stripHidden: {},
  videoOff: {},
}));

/** The stage is on screen in `channelId` (or nowhere: null). */
export function setStageOn(channelId: Snowflake | null): void {
  if (useStreamView.getState().stageOn !== channelId)
    useStreamView.setState({ stageOn: channelId });
}

/** The stage's focus in `channelId` now (kept for when it comes back). */
export function keepFocus(channelId: Snowflake, key: FocusKey | null): void {
  const cur = useStreamView.getState().focus;
  const was = cur[channelId] ?? null;
  if (was === key || (was && key && was.kind === key.kind && was.userId === key.userId)) return;
  useStreamView.setState({ focus: { ...cur, [channelId]: key } });
}

/** A stream stopped being watched: a focus on it is let go (Discord goes back to the grid). */
export function forgetStreamFocus(userId: Snowflake): void {
  const cur = useStreamView.getState().focus;
  let next: Record<Snowflake, FocusKey | null> | null = null;
  for (const [ch, k] of Object.entries(cur))
    if (k?.kind === 'stream' && k.userId === userId) (next ??= { ...cur })[ch] = null;
  if (next) useStreamView.setState({ focus: next });
}

/** Bring `userId`'s stream back in its room (the caller opens the room). */
export function askStream(channelId: Snowflake, userId: Snowflake): void {
  const n = (useStreamView.getState().ask?.n ?? 0) + 1;
  useStreamView.setState((s) => ({
    ask: { channelId, userId, n },
    focus: { ...s.focus, [channelId]: { kind: 'stream', userId } },
  }));
}

export function setMiniFolded(folded: boolean): void {
  if (useStreamView.getState().folded !== folded) useStreamView.setState({ folded });
}

/** Leave people without video off the stage in `channelId` (or show them again). */
export function setVideoOnly(channelId: Snowflake, on: boolean): void {
  useStreamView.setState((s) => ({ videoOnly: { ...s.videoOnly, [channelId]: on } }));
}

/** Fold the focus view's strip away in `channelId` (or bring it back). */
export function setStripHidden(channelId: Snowflake, on: boolean): void {
  useStreamView.setState((s) => ({ stripHidden: { ...s.stripHidden, [channelId]: on } }));
}

/** Turn `userId`'s camera off for yourself (or on again); only this client knows. */
export function setVideoOff(userId: Snowflake, off: boolean): void {
  useStreamView.setState((s) => {
    const videoOff = { ...s.videoOff };
    if (off) videoOff[userId] = true;
    else delete videoOff[userId];
    return { videoOff };
  });
}

// leaving voice: the next call starts on the grid, with the pop-out open and
// every camera on (a room's own choices, the strip and the non-video
// people, stay)
useVoice.subscribe((st, prev) => {
  if (st.session.status === prev.session.status || st.session.status !== 'idle') return;
  useStreamView.setState({ focus: {}, ask: null, folded: false, videoOff: {} });
});

/** The `stream_popout` flag (default on): the live config's, else on. */
export function popoutFlagOn(
  flags: Record<string, boolean | undefined> | undefined = useConfig.getState().config?.flags as
    Record<string, boolean | undefined> | undefined,
): boolean {
  if (flags && 'stream_popout' in flags) return flags.stream_popout === true;
  return true;
}
